import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Copy, ScanSearch } from 'lucide-react';
import { IconButton, describeError } from '@/components/primitives';
import { apiGet } from '@/pages/dashboard/api';
import { VerdictChip, reputationMap, sourcesNote } from '@/components/reputation/reputation';
import { copyText, groupIocs } from './inboxUtils';

/**
 * IOCs grouped by detected type, shown defanged (so a stray click or a
 * paste into chat never resolves them). Copy buttons copy the real value.
 * Values that are not machine-readable indicators (actor or tool names the
 * model listed) are grouped under "Other" rather than presented as IOCs.
 */
export default function IocList({ iocs, attackId }) {
  const groups = useMemo(() => groupIocs(iocs), [iocs]);
  const [rep, setRep] = useState(null); // { map, sources, counts } once checked
  const [loading, setLoading] = useState(false);
  if (!groups.length) return null;

  const copy = async (text, what) => {
    try {
      await copyText(text);
      toast.success(`Copied ${what}`);
    } catch {
      toast.error("Couldn't copy to the clipboard");
    }
  };

  // Lookup-able types only (IP, domain, URL, hash); "other" and email are never sent out.
  const lookupTypes = new Set(['ipv4', 'ipv6', 'domain', 'url', 'sha256', 'sha1', 'md5']);
  const canCheck = Boolean(attackId) && groups.some((g) => lookupTypes.has(g.type));

  const checkReputation = async (refresh = false) => {
    setLoading(true);
    try {
      const d = await apiGet(`/reputation/threat/${encodeURIComponent(attackId)}`, refresh ? { refresh: true } : undefined);
      setRep({ map: reputationMap(d.results), sources: d.sources, counts: d.counts || {} });
      const bad = (d.counts?.malicious || 0) + (d.counts?.suspicious || 0);
      toast.success(bad ? `${bad} indicator${bad === 1 ? '' : 's'} flagged by public feeds` : 'No indicators flagged by public feeds');
    } catch (err) {
      toast.error("Couldn't check reputation", { description: describeError(err) });
    } finally {
      setLoading(false);
    }
  };

  const repFor = (item) => rep?.map.get(String(item.value).toLowerCase());

  return (
    <div className="ib-iocs" data-testid="ioc-list">
      {canCheck && (
        <div className="rep-action">
          <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={() => checkReputation(false)} disabled={loading} data-testid="check-reputation">
            <ScanSearch aria-hidden="true" /> {loading ? 'Checking…' : rep ? 'Re-check reputation' : 'Check reputation'}
          </button>
          {rep ? (
            <span className="rep-summary">
              {['malicious', 'suspicious', 'benign', 'unknown'].filter((v) => rep.counts[v]).map((v) => (
                <span key={v} className={`rep-count rep-count--${v}`}>{rep.counts[v]} {v === 'unknown' ? 'no data' : v}</span>
              ))}
              <span className="rep-action-note">{sourcesNote(rep.sources)}</span>
            </span>
          ) : <span className="rep-action-note">Checks these indicators against public reputation feeds. Advisory.</span>}
        </div>
      )}
      {groups.map((g) => (
        <div key={g.type} className="ib-ioc-group">
          <div className="ib-ioc-group-head">
            <span className="ib-ioc-type">{g.type === 'other' ? 'Other (not machine-readable)' : g.label}</span>
            <span className="ib-ioc-count">{g.items.length}</span>
            {g.items.length > 1 && (
              <button
                type="button"
                className="ib-link-btn"
                onClick={() => copy(g.items.map((i) => i.value).join('\n'), `${g.items.length} ${g.label} values`)}
              >
                Copy all
              </button>
            )}
          </div>
          <ul className="ib-ioc-items">
            {g.items.map((item) => (
              <li key={item.value} className="ib-ioc-item">
                <code className="ib-ioc-value" title={g.type === 'other' ? undefined : 'Shown defanged; Copy gives the original value'}>
                  {item.display}
                </code>
                {rep && lookupTypes.has(g.type) && <VerdictChip result={repFor(item)} />}
                <IconButton
                  label={`Copy ${item.value}`}
                  icon={Copy}
                  size="sm"
                  tooltip={false}
                  onClick={() => copy(item.value, g.type === 'other' ? 'value' : g.label)}
                />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
